import React from "react";
import { useLocation, Route, Routes, Navigate } from "react-router-dom";
import { Container } from "reactstrap";

import AdminNavbar from "components/Navbars/AdminNavbar.js";
import AdminFooter from "components/Footers/AdminFooter.js";
import Sidebar from "components/Sidebar/Sidebar.js";

import routes from "routes.js";
import "../assets/css/globle.css";

const Admin = (props) => {
  const mainContent = React.useRef(null);
  const location = useLocation();

  const [isSidebarPinned, setIsSidebarPinned] = React.useState(true);

  const [userRole, setUserRole] = React.useState(() => {
    return localStorage.getItem("userRole") || "admin";
  });

  const toggleSidebar = () => {
    setIsSidebarPinned(!isSidebarPinned);
  };

  // Role & permissions synchronization
  React.useEffect(() => {
    const storedRole = localStorage.getItem("userRole") || "admin";
    setUserRole(storedRole);

    const defaultAdminPermissions = [
      "Dashboard",
      "User Management",
      "Verification Management",
      "Booking Management",
      "Reviews & Ratings",
      "Help & Support",
      "Report Management",
      "Membership Management",
      "Tour Package Management",
      "Tour Categories Management",
      "FAQ Management",
      "Privacy Policy",
      "Terms & Conditions",
      "Contact Us",
      "Role Management",
      "Profile",
    ];

    const storedPermissions = localStorage.getItem("userPermissions");
    if (!storedPermissions) {
      // Default to full permissions for super admin
      localStorage.setItem("userPermissions", JSON.stringify(defaultAdminPermissions));
    } else {
      try {
        const perms = JSON.parse(storedPermissions || "[]");
        if (Array.isArray(perms) && !perms.includes("Report Management")) {
          perms.push("Report Management");
          localStorage.setItem("userPermissions", JSON.stringify(perms));
        }
      } catch (e) {
        localStorage.setItem("userPermissions", JSON.stringify(defaultAdminPermissions));
      }
    }

    const handleRoleUpdate = () => {
      setUserRole(localStorage.getItem("userRole") || "admin");
    };

    window.addEventListener("userRoleUpdated", handleRoleUpdate);
    return () => window.removeEventListener("userRoleUpdated", handleRoleUpdate);
  }, []);

  React.useEffect(() => {
    if (isSidebarPinned) {
      document.body.classList.add("g-sidenav-pinned");
      document.body.classList.add("sidebar-pinned");
    } else {
      document.body.classList.remove("g-sidenav-pinned");
      document.body.classList.remove("sidebar-pinned");
    }
  }, [isSidebarPinned]);

  // Global Modal & Backdrop Styles
  React.useEffect(() => {
    const style = document.createElement("style");

    style.textContent = `
      .modal {
        z-index: 1060;
      }

      .modal-backdrop {
        z-index: 1055;
      }

      @media (min-width: 768px) {
        body.sidebar-pinned .modal {
          left: 260px !important;
          width: calc(100% - 260px) !important;
        }

        body.sidebar-pinned .modal-backdrop {
          left: 260px !important;
          width: calc(100% - 260px) !important;
        }
      }
    `;

    document.head.appendChild(style);

    return () => document.head.removeChild(style);
  }, []);

  React.useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;

    if (mainContent.current) {
      mainContent.current.scrollTop = 0;
    }
  }, [location]);

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout !== "/admin") return null;

      const role = userRole?.toLowerCase();

      // Admin / Super Admin
      const isSuperAdmin = (() => {
        try {
          const userData = JSON.parse(localStorage.getItem("userData") || "{}");
          if (userData?.isSuperAdmin === true) return true;
          if (
            userData?.type === "superadmin" ||
            userData?.type === "super_admin" ||
            userData?.roleName?.toLowerCase()?.includes("super")
          ) {
            return true;
          }
          const r = (
            role ||
            localStorage.getItem("userRole") ||
            userData?.roleName ||
            userData?.type ||
            ""
          )
            .toLowerCase()
            .trim();
          if (
            r === "super admin" ||
            r === "superadmin" ||
            r === "super_admin" ||
            r.includes("super")
          ) {
            return true;
          }
          return false;
        } catch (e) {
          return false;
        }
      })();

      if (!isSuperAdmin) {
        const allowedPaths = ["/profile"];

        let userPermissions = [];

        try {
          userPermissions = JSON.parse(
            localStorage.getItem("userPermissions") || "[]"
          );
        } catch (e) {
          userPermissions = [];
        }

        const isRouteAllowed = (routeProp) => {
          if (routeProp.path === "/rooms-chats/:id") {
            return true;
          }

          if (!routeProp.name) {
            const parentPath = routeProp.path.split("/:")[0];

            const parentRoute = routes.find(
              (r) => r.path === parentPath
            );

            if (parentRoute && parentRoute.name) {
              return (
                allowedPaths.includes(parentRoute.path) ||
                userPermissions.includes(parentRoute.name)
              );
            }

            return true;
          }

          return (
            allowedPaths.includes(routeProp.path) ||
            userPermissions.includes(routeProp.name)
          );
        };

        if (!isRouteAllowed(prop)) {
          return null;
        }
      }

      return (
        <Route
          path={prop.path}
          element={prop.component}
          key={key}
        />
      );
    });
  };

  const getBrandText = (path) => {
    for (let i = 0; i < routes.length; i++) {
      if (
        path.indexOf(
          routes[i].layout + routes[i].path
        ) !== -1
      ) {
        return routes[i].name;
      }
    }

    return "Dashboard";
  };

  const getFallbackRedirect = () => {
    const role = userRole?.toLowerCase();

    const isSuperAdmin =
      role === "super admin" || role === "admin";

    if (isSuperAdmin) {
      return "/admin/index";
    }

    let userPermissions = [];

    try {
      userPermissions = JSON.parse(
        localStorage.getItem("userPermissions") || "[]"
      );
    } catch (e) {
      userPermissions = [];
    }

    const firstAllowedRoute = routes.find((route) => {
      if (route.layout !== "/admin" || !route.name) {
        return false;
      }

      return userPermissions.includes(route.name);
    });

    return firstAllowedRoute
      ? `/admin${firstAllowedRoute.path}`
      : "/admin/profile";
  };

  return (
    <>
      <Sidebar
        {...props}
        location={location}
        routes={routes}
        isPinned={isSidebarPinned}
        logo={{
          innerLink: "/admin/index",
          imgSrc: require("../assets/img/icons/common/image__5_-removebg-preview 1.png"),
          imgAlt: "...",
        }}
      />

      <div className="main-content" ref={mainContent}>
        <AdminNavbar
          {...props}
          brandText={getBrandText(location.pathname)}
          isSidebarPinned={isSidebarPinned}
          toggleSidebar={toggleSidebar}
        />

        <Routes>
          <Route
            path="/"
            element={
              <Navigate
                to="/admin/index"
                replace
              />
            }
          />

          {getRoutes(routes)}

          <Route
            path="*"
            element={
              <Navigate
                to={getFallbackRedirect()}
                replace
              />
            }
          />
        </Routes>

        <Container fluid>
          <AdminFooter />
        </Container>
      </div>
    </>
  );
};

export default Admin;
